import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, inr } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";

export default function Checkout() {
  const nav = useNavigate();
  const { user, cart, clearCart } = useApp();
  const [addr, setAddr] = useState({ line1: "", line2: "", city: "", state: "", pincode: "", phone: "" });
  const [name, setName] = useState(user?.name || "");
  const [payment, setPayment] = useState("cod");
  const [coupon, setCoupon] = useState("");
  const [quote, setQuote] = useState(null);
  const [placing, setPlacing] = useState(false);

  useEffect(() => {
    if (!user) nav("/login?next=/checkout");
  }, [user, nav]);
  useEffect(() => {
    if (cart.length === 0) return;
    api.post("/orders/quote", { items: cart, coupon_code: coupon }).then((r) => setQuote(r.data));
  }, [cart, coupon]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Please enter your name");
    for (const k of ["line1", "city", "state", "pincode", "phone"]) {
      if (!addr[k].trim()) return toast.error(`Missing ${k}`);
    }
    setPlacing(true);
    try {
      const r = await api.post("/orders", {
        items: cart, address: addr, coupon_code: coupon,
        payment_method: payment, customer_name: name,
      });
      clearCart();
      toast.success("Order placed!");
      nav(`/order-success/${r.data.order_number}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Order failed");
    } finally { setPlacing(false); }
  };

  if (!user || cart.length === 0) return <div className="p-10 text-center text-muted-foreground">Redirecting…</div>;

  return (
    <div className="max-w-7xl mx-auto px-6 py-12 grid lg:grid-cols-[1fr_400px] gap-10">
      <form onSubmit={submit} className="space-y-8">
        <h1 className="font-display text-4xl font-black">Checkout</h1>

        <section>
          <p className="label-tag mb-4">Contact</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Full Name</Label><Input data-testid="input-name" value={name} onChange={(e) => setName(e.target.value)} required /></div>
            <div><Label>Email</Label><Input value={user.email} disabled /></div>
          </div>
        </section>

        <section>
          <p className="label-tag mb-4">Shipping Address</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label>Address line 1</Label><Input data-testid="input-line1" value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} required /></div>
            <div className="sm:col-span-2"><Label>Address line 2 (optional)</Label><Input data-testid="input-line2" value={addr.line2} onChange={(e) => setAddr({ ...addr, line2: e.target.value })} /></div>
            <div><Label>City</Label><Input data-testid="input-city" value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} required /></div>
            <div><Label>State</Label><Input data-testid="input-state" value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value })} required /></div>
            <div><Label>PIN Code</Label><Input data-testid="input-pincode" value={addr.pincode} onChange={(e) => setAddr({ ...addr, pincode: e.target.value })} required /></div>
            <div><Label>Phone (WhatsApp)</Label><Input data-testid="input-phone" value={addr.phone} onChange={(e) => setAddr({ ...addr, phone: e.target.value })} required /></div>
          </div>
        </section>

        <section>
          <p className="label-tag mb-4">Payment Method</p>
          <RadioGroup value={payment} onValueChange={setPayment} className="space-y-3">
            <label className="flex items-center gap-3 p-4 border border-white/15 rounded-md cursor-pointer hover:border-[#D4B25C]">
              <RadioGroupItem data-testid="pay-cod" value="cod" />
              <div>
                <p className="font-semibold">Cash on Delivery</p>
                <p className="text-xs text-muted-foreground">Pay when you receive · No online payment</p>
              </div>
            </label>
            <label className="flex items-center gap-3 p-4 border border-white/15 rounded-md cursor-pointer hover:border-[#D4B25C] opacity-70">
              <RadioGroupItem data-testid="pay-razorpay" value="razorpay" disabled />
              <div>
                <p className="font-semibold">Razorpay (UPI, Cards, Wallets) <span className="text-xs text-muted-foreground">— coming soon</span></p>
                <p className="text-xs text-muted-foreground">Requires Razorpay merchant keys · Setup via Admin</p>
              </div>
            </label>
          </RadioGroup>
        </section>

        <Button data-testid="place-order-btn" type="submit" disabled={placing} className="w-full h-12 bg-[#8B2E2E] hover:bg-[#6f2323] uppercase tracking-widest text-xs">
          {placing ? "Placing order…" : `Place Order · ${inr(quote?.total || 0)}`}
        </Button>
      </form>

      <aside className="lg:sticky lg:top-32 lg:self-start bg-[#2A231D] border border-[#D4B25C]/15 rounded-md p-6">
        <p className="label-tag mb-4">Your Bag</p>
        <div className="space-y-3 max-h-72 overflow-auto no-scrollbar">
          {cart.map((it, i) => (
            <div key={i} className="flex gap-3 text-sm">
              <img src={it.image} alt="" className="h-16 w-14 object-cover rounded" />
              <div className="flex-1">
                <p className="line-clamp-1">{it.name}</p>
                <p className="text-xs text-muted-foreground">Size {it.size} · Qty {it.qty}</p>
              </div>
              <span className="font-semibold">{inr(it.price * it.qty)}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 flex gap-2">
          <Input placeholder="Coupon" value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} data-testid="checkout-coupon" />
        </div>
        {quote && (
          <div className="mt-4 space-y-2 text-sm border-t border-black/10 pt-4">
            <div className="flex justify-between"><span>Subtotal</span><span>{inr(quote.subtotal)}</span></div>
            {quote.b2g1_discount > 0 && <div className="flex justify-between text-[#8B2E2E]"><span>Buy 2 Get 1</span><span>−{inr(quote.b2g1_discount)}</span></div>}
            {quote.coupon_discount > 0 && <div className="flex justify-between text-[#8B2E2E]"><span>Coupon</span><span>−{inr(quote.coupon_discount)}</span></div>}
            <div className="flex justify-between"><span>GST (5%)</span><span>{inr(quote.gst)}</span></div>
            <div className="flex justify-between font-bold text-lg border-t border-black/10 pt-2"><span>Total</span><span>{inr(quote.total)}</span></div>
          </div>
        )}
      </aside>
    </div>
  );
}
